import { useSessionMetadataValues } from "contexts/SessionMetadata/context";
import { withSessionDetailPairs } from "lib/session-metadata";
import type { OrbExtension } from "lib/orb-qr";
import { useContext, useEffect, useMemo, useState } from "react";
import { decodeJwt } from "jose";
import { AuthContext } from "contexts/AuthContext";
import {
  buildOrbQrContent,
  getOrbDetailPairs,
  OrbDetailValue,
} from "lib/orb-qr";
import { getCurrentSessionId } from "lib/session";
import { Storage, StorageKey } from "lib/storage";

export function useOrbQrContent({
  captureTaskId,
  extension,
  storedDetails,
  stepDetails,
  includeDetails,
  detailsLoaded,
  sessionMetadata,
}: {
  sessionMetadata?: Record<string, string>;
  captureTaskId?: string;
  extension: OrbExtension | null;
  storedDetails: any;
  stepDetails?: OrbDetailValue[];
  includeDetails: boolean;
  detailsLoaded: boolean;
}) {
  const { token } = useContext(AuthContext);
  const [userId, setUserId] = useState<string>();
  const [sessionId, setSessionId] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    try {
      const storedToken = Storage.get(StorageKey.TOKEN);
      if (!storedToken) throw new Error("Authentication token is missing");

      const decoded = decodeJwt(storedToken);
      if (!decoded.registered || !decoded.sub) {
        throw new Error("Registered user ID is missing");
      }

      setUserId(decoded.sub.toString());
      setError(undefined);
    } catch (decodeError) {
      console.error(
        "Could not read the authenticated user for Orb QR",
        decodeError
      );
      setUserId(undefined);
      setError("The QR code could not be generated. Please sign in again.");
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setSessionId(undefined);
    getCurrentSessionId(token?.contributor)
      .then((value) => {
        if (cancelled) return;
        if (!value) {
          setError("The QR code could not be generated. Please try again.");
          return;
        }
        setSessionId(value);
      })
      .catch((sessionError) => {
        console.error("Could not create the Orb session ID", sessionError);
        if (!cancelled) {
          setError("The QR code could not be generated. Please try again.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token?.contributor, userId]);

  const activeSessionMetadata = useSessionMetadataValues();
  const sessionValues = JSON.stringify(
    sessionMetadata ?? activeSessionMetadata
  );
  const detailPairs = useMemo(
    () =>
      withSessionDetailPairs(
        includeDetails ? getOrbDetailPairs(storedDetails, stepDetails) : [],
        JSON.parse(sessionValues)
      ),
    [includeDetails, stepDetails, storedDetails, sessionValues]
  );

  const content = useMemo(() => {
    if (!userId || !sessionId || !detailsLoaded || extension === null) {
      return null;
    }

    return buildOrbQrContent({
      userId,
      sessionId,
      captureTaskId,
      contributor: token?.contributor,
      detailPairs,
      extension,
    });
  }, [
    captureTaskId,
    detailPairs,
    detailsLoaded,
    extension,
    sessionId,
    token?.contributor,
    userId,
  ]);

  return {
    content,
    error,
    isReady: Boolean(content),
    userId,
    sessionId,
    collectionId: token?.contributor,
  };
}
